import { AfterViewInit, Component, OnInit, ViewChild, inject } from '@angular/core';
import { AbstractControl, FormBuilder, ReactiveFormsModule, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { MatPaginator } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { TranslateModule } from '@ngx-translate/core';
import { MessageService } from '@igo2/core/message';
import { LanguageService } from '@igo2/core/language';
import { MaterialImports } from 'src/app/shared/imports/material.imports';
import { Constantes } from 'src/app/shared/constantes';
import { ParametrePilotage, ValeurParametrePilotage } from '../pilotage.models';
import { PilotageService } from '../pilotage.service';

type Statut = 'enVigueur' | 'aVenir' | 'echue';

/** Ligne du tableau : une valeur, avec la description de son paramètre. */
interface LigneValeur extends ValeurParametrePilotage {
  description: string;
  statut: Statut;
}

/**
 * Onglet de pilotage des valeurs de paramètres (SIG_VALEU_PARAM) : ajout et
 * modification, sans suppression (une valeur se retire en lui donnant une
 * date de fin). Une valeur est en vigueur du jour de début au jour de fin
 * inclus ; sans date de fin, elle le reste.
 */
@Component({
  selector: 'pilotage-parametres',
  imports: [
    ReactiveFormsModule,
    TranslateModule,
    ...MaterialImports
  ],
  templateUrl: './pilotage-parametres.component.html',
  styleUrls: ['./pilotage-parametres.component.scss']
})
export class PilotageParametresComponent implements OnInit, AfterViewInit {
  #service = inject(PilotageService);
  #messageService = inject(MessageService);
  #languageService = inject(LanguageService);
  #fb = inject(FormBuilder);

  parametres: ParametrePilotage[] = [];
  dataSource = new MatTableDataSource<LigneValeur>([]);
  /** Valeur en cours de modification ; null = ajout. */
  enEdition: LigneValeur | null = null;
  @ViewChild(MatPaginator) paginator!: MatPaginator;

  displayedColumns = ['parametre', 'valeur', 'dateDebut', 'dateFin', 'statut', 'actions'];

  formGroup = this.#fb.group({
    parametreCode: ['', Validators.required],
    valeur: ['', [Validators.required, this.longueurValidator()]],
    dateDebut: [new Date() as Date | null, Validators.required],
    dateFin: [null as Date | null]
  }, { validators: this.dateRangeValidator() });

  ngOnInit(): void {
    this.charger();
    this.formGroup.controls.parametreCode.valueChanges.subscribe(() =>
      this.formGroup.controls.valeur.updateValueAndValidity()
    );
  }

  ngAfterViewInit(): void {
    this.dataSource.paginator = this.paginator;
  }

  /** Longueur maximale imposée au paramètre choisi, ou null s'il n'en a pas. */
  get longueurMax(): number | null {
    return this.formGroup?.controls.parametreCode.value === Constantes.Parametres.BandeauMessageAccueil
      ? Constantes.Parametres.BandeauMessageAccueilLongueurMax
      : null;
  }

  startEdit(ligne: LigneValeur): void {
    this.enEdition = ligne;
    this.formGroup.reset({
      parametreCode: ligne.parametreCode,
      valeur: ligne.valeur,
      dateDebut: this.versDate(ligne.dateDebutEffective),
      dateFin: this.versDate(ligne.dateFinEffective)
    });
    this.formGroup.controls.parametreCode.disable();
  }

  clearFormFields(): void {
    this.enEdition = null;
    this.formGroup.controls.parametreCode.enable();
    this.formGroup.reset({ parametreCode: '', valeur: '', dateDebut: new Date(), dateFin: null });
  }

  addOrUpdate(): void {
    if (this.formGroup.invalid) {
      return;
    }
    const v = this.formGroup.getRawValue();
    const valeur: ValeurParametrePilotage = {
      valeurParametreId: this.enEdition?.valeurParametreId ?? 0,
      parametreCode: v.parametreCode,
      valeur: v.valeur.trim(),
      dateDebutEffective: this.versTexte(v.dateDebut),
      dateFinEffective: this.versTexte(v.dateFin)
    };
    const requete = this.enEdition
      ? this.#service.modifierValeurParametre(valeur)
      : this.#service.ajouterValeurParametre(valeur);

    requete.subscribe({
      next: () => {
        this.clearFormFields();
        this.charger();
      },
      error: () => this.#messageService.error(this.traduire('erreurEnregistrement'), this.traduire('erreur'))
    });
  }

  private charger(): void {
    this.#service.obtenirParametres().subscribe({
      next: parametres => {
        this.parametres = parametres;
        this.dataSource.data = parametres.flatMap(p => p.valeurs.map(v => ({
          ...v,
          description: p.description,
          statut: this.statut(v)
        })));
      },
      error: () => this.#messageService.error(this.traduire('erreurChargement'), this.traduire('erreur'))
    });
  }

  private statut(v: ValeurParametrePilotage): Statut {
    const aujourdhui = this.versTexte(new Date());
    const debut = v.dateDebutEffective?.substring(0, 10);
    const fin = v.dateFinEffective?.substring(0, 10);
    if (debut && debut > aujourdhui) return 'aVenir';
    if (fin && fin < aujourdhui) return 'echue';
    return 'enVigueur';
  }

  private longueurValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const max = this.longueurMax;
      const longueur = (control.value ?? '').trim().length;
      return max !== null && longueur > max ? { longueurMax: { max, longueur } } : null;
    };
  }

  private dateRangeValidator(): ValidatorFn {
    return (group: AbstractControl): ValidationErrors | null => {
      const debut = this.versTexte(group.get('dateDebut')?.value);
      const fin = this.versTexte(group.get('dateFin')?.value);
      return debut && fin && fin < debut ? { dateInvalide: true } : null;
    };
  }

  /** Date du calendrier vers « aaaa-mm-jj », sans passer par l'UTC (pas de décalage d'un jour). */
  private versTexte(date: Date | null | undefined): string | null {
    if (!date) return null;
    const mois = String(date.getMonth() + 1).padStart(2, '0');
    const jour = String(date.getDate()).padStart(2, '0');
    return `${date.getFullYear()}-${mois}-${jour}`;
  }

  /** « aaaa-mm-jj… » reçu de l'API vers une date locale à minuit. */
  private versDate(texte: string | null): Date | null {
    if (!texte) return null;
    const [annee, mois, jour] = texte.substring(0, 10).split('-').map(Number);
    return new Date(annee, mois - 1, jour);
  }

  private traduire(cle: string): string {
    return this.#languageService.translate.instant(`pilotageParametres.messages.${cle}`);
  }
}
